/**
 * Per-case `FilterStore` provider.
 *
 * Mounts a fresh `createFilterStore` instance keyed by `caseId`. When the
 * caseId changes (route navigation), the previous store is torn down and a
 * new one is created — so filter state from one case never leaks into
 * another. All Investigate views consume the store via `useFilterStore`.
 */

import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { useStore } from 'zustand'
import type { StoreApi } from 'zustand/vanilla'
import { fetchPatternUnion } from '@/services/api/pattern-query-union'
import { type ChipFetcher, type FilterState, getOrCreateFilterStore } from './filter-store'

const FilterStoreContext = createContext<StoreApi<FilterState> | null>(null)

const defaultFetcher: ChipFetcher = ({ caseId, chips, carry }) =>
  fetchPatternUnion(caseId, chips, carry)

interface FilterStoreProviderProps {
  caseId: string
  fetcher?: ChipFetcher
  children: ReactNode
}

export function FilterStoreProvider({
  caseId,
  fetcher = defaultFetcher,
  children,
}: FilterStoreProviderProps) {
  const store = useMemo(() => getOrCreateFilterStore({ caseId, fetcher }), [caseId, fetcher])
  return <FilterStoreContext.Provider value={store}>{children}</FilterStoreContext.Provider>
}

export function useFilterStore<T>(selector: (state: FilterState) => T): T {
  const store = useContext(FilterStoreContext)
  if (!store) {
    throw new Error(
      'useFilterStore must be used inside <FilterStoreProvider> (typically mounted under InvestigateLayout)'
    )
  }
  return useStore(store, selector)
}

/** Imperative access for callers that need the store API (e.g. event handlers
 * outside the React render path). Prefer `useFilterStore` selectors. */
export function useFilterStoreApi(): StoreApi<FilterState> {
  const store = useContext(FilterStoreContext)
  if (!store) {
    throw new Error('useFilterStoreApi must be used inside <FilterStoreProvider>')
  }
  return store
}

/** Same as `useFilterStoreApi`, but returns `null` when no provider is mounted
 * instead of throwing. For hooks like `useGraphExclusion` that are reused on
 * surfaces outside `InvestigateLayout` (e.g. `case-graph-explorer`). */
export function useFilterStoreApiOptional(): StoreApi<FilterState> | null {
  return useContext(FilterStoreContext)
}
